import { useMemo } from 'react';
import { useLocation } from 'react-router';

type QueryParamsOptions = {
    parseArrays?: boolean;
};

export const getQueryParamsFromUrl = <
    T extends Record<string, string | string[]> = Record<string, string | string[]>,
>(
    url: string,
    opts?: QueryParamsOptions,
): T => {
    const query = new URLSearchParams(url);
    return Array.from(query.keys()).reduce(
        (result, queryKey) => {
            if (opts?.parseArrays && query.getAll(queryKey).length > 1) {
                result[queryKey] = query.getAll(queryKey) as string[];
            } else {
                result[queryKey] = query.get(queryKey) ?? '';
            }
            return result;
        },
        {} as Record<string, unknown>,
    ) as T;
};

export function useQueryParams<T = Record<string, string>>(opts?: QueryParamsOptions): T;
export function useQueryParams<T = Record<string, string | string[]>>(opts: {
    parseArrays: true;
}): T;

/**
 * A hook that provides the current URL's query parameters.
 *
 * @template T - The expected shape of the query parameters object
 * @param {QueryParamsOptions} [opts] - Options for parsing query parameters
 * @param {boolean} [opts.parseArrays] - If true, parses repeated keys as arrays
 * @returns {T} An object containing the current URL's query parameters
 *
 * @example
 * ```typescript
 * // Basic usage
 * const params = useQueryParams();
 * console.log(params.name); // 'James' (if URL is '?name=James')
 *
 * // With type specification
 * interface MyParams {
 *   page: string;
 *   sort?: string;
 * }
 * const { page, sort } = useQueryParams<MyParams>();
 *
 * // With array parsing
 * const { tags } = useQueryParams<{ tags: string[] }>({ parseArrays: true });
 * console.log(tags); // ['react', 'typescript'] (if URL is '?tags=react&tags=typescript')
 * ```
 *
 * @remarks
 * - This hook updates whenever the URL's search string changes.
 */
export function useQueryParams<
    T extends Record<string, string | string[]> = Record<string, string | string[]>,
>(opts?: QueryParamsOptions): T {
    const { search } = useLocation();

    return useMemo(() => {
        return getQueryParamsFromUrl(search, opts);
    }, [search, opts?.parseArrays]);
}
